import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenu } from '@angular/material/menu';
import { AsYouType, CountryCode as CC, NationalNumber, PhoneNumber, getExampleNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { CountryCode, Examples } from '../..//data/country-code';
import { Country } from '../../models/country.model';
import { PhoneNumberFormat } from '../../models/phone-number-format.model';

@Component({
  selector: 'app-intl-phone-input',
  templateUrl: './intl-phone-input.component.html',
  styleUrls: ['./intl-phone-input.component.scss'],
  providers: [
    CountryCode,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntlPhoneInputComponent),
      multi: true
    }
  ],
})
export class IntlPhoneInputComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('focusable') focusable?: HTMLInputElement

  @Input() providedValue: string | undefined;
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();

  @Input() preferredCountries: Array<string> = [];
  @Input() enablePlaceholder = true;
  @Input() inputPlaceholder: string | undefined;
  @Input() onlyCountries: Array<string> = [];
  @Input() enableSearch = false;
  @Input() searchPlaceholder: string | undefined;
  @Input() label = '';
  @Input() name: string | undefined;
  @Input() classList: string | undefined;

  @Input()
  get format(): PhoneNumberFormat {
    return this._format;
  }

  set format(value: PhoneNumberFormat) {
    this._format = value;
    this.phoneNumber = this.formattedPhoneNumber as NationalNumber;
  }

  @ViewChild(MatMenu) matMenu: MatMenu | undefined;
  private _placeholder: string | undefined;
  private _required = false;
  private _disabled = false;
  phoneNumber: NationalNumber | undefined;
  allCountries: Array<Country> = [];
  preferredCountriesInDropDown: Array<Country> = [];
  selectedCountry: Country | undefined;
  numberInstance: PhoneNumber | undefined;
  @Output() countryChanged = new EventEmitter<Country>();

  private previousFormattedNumber: string | undefined;
  private _format: PhoneNumberFormat = 'international';
  focused: boolean = false;

  static getPhoneNumberPlaceHolder(countryISOCode: CC): string | undefined {
    const result = getExampleNumber(countryISOCode, Examples);
    return !!result ? result.number.toString() : undefined;
  }

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private countryCodeData: CountryCode,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
  ) {
    fm.monitor(elRef, true).subscribe((origin) => {
      this.focused = !!origin;
    });
    this.fetchCountryData();
    this.setupIOSCompatibility();
  }

  ngOnInit(): void {
    if (!this.searchPlaceholder) {
      this.searchPlaceholder = 'Search ...';
    }
    if (this.preferredCountries.length) {
      this.preferredCountries.forEach((iso2) => {
        const preferredCountry = this.allCountries
          .filter((c) => c.iso2 === iso2)
          .shift();
        if (preferredCountry) {
          this.preferredCountriesInDropDown.push(preferredCountry);
        }
      });
    }
    if (this.onlyCountries.length) {
      this.allCountries = this.allCountries.filter((c) =>
        this.onlyCountries.includes(c.iso2)
      );
    }
    if (this.numberInstance?.country) {
      // If an existing number is present, we use it to determine selectedCountry
      this.selectedCountry = this.getCountry(this.numberInstance.country);
    } else {
      if (this.preferredCountriesInDropDown.length) {
        this.selectedCountry = this.preferredCountriesInDropDown[0];
      } else {
        this.selectedCountry = this.allCountries[0];
      }
    }
    this.countryChanged.emit(this.selectedCountry);
    /* if (this.required) {
      this.placeholder = this.placeholder + '*';
    } */
    this._changeDetectorRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['providedValue'].currentValue !== this.phoneNumber) {
      this.phoneNumber = changes['providedValue'].currentValue;
      this.onPhoneNumberChange();
    }
  }



  public onPhoneNumberChange(): void {
    try {
      this.numberInstance = parsePhoneNumberFromString(
        this.phoneNumber?.toString() ?? '',
        this.selectedCountry?.iso2.toUpperCase() as CC
      );
      this.formatAsYouTypeIfEnabled();
      this.providedValue = this.numberInstance?.number;

      if (this.numberInstance?.isValid()) {
        if (this.phoneNumber !== this.formattedPhoneNumber) {
          this.phoneNumber = this.formattedPhoneNumber as NationalNumber;
        }
        if (
          this.selectedCountry?.iso2 !== this.numberInstance.country &&
          !!this.numberInstance.country
        ) {
          this.selectedCountry = this.getCountry(this.numberInstance.country);
          this.countryChanged.emit(this.selectedCountry);
        }
      }
    } catch (e) {
      console.error(e);
      // if no possible numbers are there,
      // then the full number is passed so that validator could be triggered and proper error could be shown
      this.providedValue = this.phoneNumber?.toString();
    }
    this.valueChange.emit(this.providedValue);
    this._changeDetectorRef.markForCheck();
  }

  public onCountrySelect(country: Country, el: HTMLInputElement): void {
    if (this.phoneNumber) {
      this.phoneNumber = this.numberInstance?.nationalNumber;
    }
    this.selectedCountry = country;
    this.countryChanged.emit(this.selectedCountry);
    this.onPhoneNumberChange();
    setTimeout(() => {
      el.focus();
    }, 0);
  }

  private setupIOSCompatibility(): void {
    // Add iOS-specific touch event handling
    if (this.isIOS()) {
      // Ensure proper touch event handling for the country selector button
      setTimeout(() => {
        const countrySelector = this.elRef.nativeElement.querySelector('.country-selector');
        if (countrySelector) {
          // Add touch event listeners for better iOS compatibility
          countrySelector.addEventListener('touchstart', (e) => {
            e.preventDefault();
          }, { passive: false });
          
          countrySelector.addEventListener('touchend', (e) => {
            e.preventDefault();
            // Trigger click programmatically for iOS
            (countrySelector as HTMLElement).click();
          }, { passive: false });
        }
      }, 100);

      // Handle keyboard visibility changes for better dropdown positioning
      this.setupKeyboardAwareness();
    }
  }

  private setupKeyboardAwareness(): void {
    // Listen for resize events (keyboard show/hide triggers this on iOS)
    window.addEventListener('resize', this.handleViewportChange);
    
    // Also listen for orientation changes
    window.addEventListener('orientationchange', this.handleOrientationChange);
  }

  private isIOS(): boolean {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const touchable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Classic iOS devices or mobile UA
    let isiOS = /iPad|iPhone|iPod/i.test(platform) ||
      /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);      // iOS Chrome/Firefox/Edge

    // iPadOS 13+ spoofs Mac but has touch
    if (!isiOS && platform === 'MacIntel' && touchable) {
      isiOS = true;
    }

    return isiOS;
  }

  public getCountry(code: string): Country {
    return (
      this.allCountries.find((c) => c.iso2 === code.toLowerCase()) || {
        name: 'UN',
        iso2: 'UN',
        dialCode: '',
        priority: 0,
        areaCodes: undefined,
        flagClass: 'UN',
        placeHolder: '',
      }
    );
  }

  public onInputKeyPress(event: KeyboardEvent): void {
    const pattern = /[0-9\- ]/;

    if (!pattern.test(event.key)) {
      event.preventDefault();
    } else {
      this.onPhoneNumberChange();
    }

  }

  protected fetchCountryData(): void {
    this.countryCodeData.allCountries.forEach((c) => {
      const country: Country = {
        name: c[0].toString(),
        iso2: c[1].toString(),
        dialCode: c[2].toString(),
        priority: +c[3] || 0,
        areaCodes: (c[4] as string[]) || undefined,
        flagClass: c[1].toString().toUpperCase(),
        placeHolder: '',
      };

      if (this.enablePlaceholder) {
        country.placeHolder =
          IntlPhoneInputComponent.getPhoneNumberPlaceHolder(
            country.iso2.toUpperCase() as CC
          ) || '';
      }
      this.allCountries.push(country);
    });
  }

  protected formatAsYouTypeIfEnabled(): void {
    if (this.enablePlaceholder && this.selectedCountry) {
      const formatter = new AsYouType(this.selectedCountry?.iso2.toUpperCase() as CC);
      const newInput = formatter.input(this.phoneNumber || '');
      if (newInput !== this.previousFormattedNumber) {
        this.phoneNumber = newInput as NationalNumber;
        this.previousFormattedNumber = newInput;
      }
    }
  }

  get formattedPhoneNumber(): string | undefined {
    switch (this.format) {
      case 'default':
        return this.numberInstance?.formatInternational();
      case 'national':
        return this.numberInstance?.formatNational();
      case 'international':
        return this.numberInstance?.formatInternational();
      default:
        return this.phoneNumber?.toString();
    }
  }

  @Input()
  get placeholder(): string {
    return this._placeholder || '';
  }

  set placeholder(value: string) {
    this._placeholder = value;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this._changeDetectorRef.markForCheck();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused;
  }


  // ControlValueAccessor implementation
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(value: any): void {
    this.providedValue = value;
    this.onPhoneNumberChange(); // Ensure numberInstance and formattedPhoneNumber are updated
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }


  ngOnDestroy(): void {
    this.fm.stopMonitoring(this.elRef);
    // Clean up iOS-specific event listeners
    if (this.isIOS()) {
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('orientationchange', this.handleOrientationChange);
    }
  }

  private handleViewportChange = () => {
    // Small delay to allow for keyboard animation
    setTimeout(() => {
      const menuPanel = document.querySelector('.ngx-mat-tel-input-mat-menu-panel');
      if (menuPanel) {
        // Force repositioning of the menu when keyboard appears/disappears
        const rect = this.elRef.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // If the input is in the lower half of the screen, adjust menu position
        if (rect.bottom > viewportHeight / 2) {
          (menuPanel as HTMLElement).style.transform = 'translateY(-50%)';
        } else {
          (menuPanel as HTMLElement).style.transform = '';
        }
      }
    }, 300);
  };

  private handleOrientationChange = () => {
    setTimeout(this.handleViewportChange, 500);
  };
}

